import angular, { IPromise, IScope } from 'angular'

import { EVENT } from '../../constants'
import {
  $compile,
  $document,
  $q,
  $rootScope,
  $timeout,
  $window,
} from '../../ng-compat/imports'
import {
  FRENCH_CANADIAN_LOCALE,
  RECEIPT_TEMPLATE_SUFFIX,
  hasPrefixedReceiptTemplateTypes,
} from '../../managers/web-register-tx-manager'
import { env } from '../../../../lib/env'
import { ReceiptData } from '../../managers/sale-manager'
import { ReceiptTemplateTypesStore } from '../../factories/database/receipt-template-types-store'
import { ReceiptSettingsStore } from '../../factories/database/receipt-settings-store'

export const WR_PRINTER_ID = 'wr-printer'

/**
 * Directive for constructing and printing different 'printable' directives, such as cash movements for cash management.
 *
 * @module webregister.directives.wrPrinter
 */
angular.module('webregister.directives').directive('wrPrinter', function () {
  return {
    restrict: 'E',
    bindToController: true,
    controllerAs: 'printerCtrl',
    controller(
      this: $TSFixMe,
      $scope: IScope,
      receiptTemplateTypesStore: ReceiptTemplateTypesStore,
      receiptSettingsStore: ReceiptSettingsStore
    ) {
      const WR_PRINTER_TIMEOUT = 2000

      /**
       * Constructs and appends the provided print directive to .wr-primary-view. Removed after a given amount of time
       * so as to ensure a correct print in Safari.
       *
       * @private
       * @method _print
       *
       * @param  {Object} options
       *
       *         @param {String} options.type
       *                The identifying type of print element
       *
       *         @param {Object} options.data
       *                The data to be passed to the directive
       */
      this._print = function (options: {
        type: string
        data?: Readonly<ReceiptData>
      }): IPromise<void> {
        if (!options || !options.type) {
          return Promise.reject(new Error('No print type provided'))
        }
        this.printData = options.data

        if (options.type === 'receipt-settings-and-template') {
          const templateID = this.printData.selectedReceiptId
            ? this.printData.selectedReceiptId
            : this.printData.register.receipt_template_id
          const directive = `receipt-print-selected`
          return receiptSettingsStore
            .get(templateID)
            .then((result: $TSFixMe) => {
              this.printOptions = result

              const receiptSuffix =
                result.locale === FRENCH_CANADIAN_LOCALE &&
                hasPrefixedReceiptTemplateTypes
                  ? RECEIPT_TEMPLATE_SUFFIX
                  : ''

              return receiptTemplateTypesStore.get(
                this.printData.paymentData &&
                  this.printData.paymentData.isSignatureSlipReceipt === true
                  ? `slip_signature${receiptSuffix}`
                  : `${result.default_type}${receiptSuffix}`
              )
            })
            .then(async templateType => {
              if (!templateType) {
                return Promise.reject(new Error('Invalid template type'))
              }
              this.printOptions.templateHtml = templateType.html
              this.printOptions.styling = templateType.css
              return this._compileDirective(directive)
            })
        } else if (options.type === 'styled-gift-receipt') {
          const directive = `receipt-print-selected`
          const templateID = this.printData.register.receipt_template_id
          return receiptSettingsStore
            .get(templateID)
            .then((result: $TSFixMe) => {
              this.printOptions = result
              this.printOptions.isGiftReceipt = true
              return receiptTemplateTypesStore
                .get('gift')
                .then(async templateType => {
                  if (!templateType) {
                    return Promise.reject(new Error('Invalid template type'))
                  }
                  this.printOptions.templateHtml = templateType.html
                  this.printOptions.styling = templateType.css
                  return this._compileDirective(directive)
                })
            })
        } else if (options.type === 'quote-print') {
          this.printOptions = options.data
          const directive = 'quote-print'
          return this._compileDirective(directive)
        } else {
          const directive = `wr-print-${options.type}`
          return this._compileDirective(directive)
        }
      }
      this._compileDirective = function (directive: string): IPromise<void> {
        this.compiledPrintDirective = $compile(`<${directive}
          id="${WR_PRINTER_ID}"
          class="vd-show-print"
          data="printerCtrl.printData"
          options="printerCtrl.printOptions">
        </${directive}>`)($scope)

        // Cancel timeout and remove the printer element
        $timeout.cancel(this._removePrinterElementTimer)
        this._removePrinterElement()
        angular
          .element(($document[0] as Document).body)
          .append(this.compiledPrintDirective)

        return $timeout(() => {
          const images = Array.from(
            this.compiledPrintDirective[0].querySelectorAll(
              'img'
            ) as HTMLImageElement[]
          ).filter(img => !img.complete)

          if (images.length) {
            // wait up to 1500ms for images to load
            const timeout = $timeout(1500, false)
            const promises = images.map(image =>
              $q(resolve => {
                const $image = angular.element(image)
                $image.one('load', resolve)
                // still print if the image fails to load
                $image.one('error', resolve)
              })
            )
            return $q.race([
              timeout,
              ($q.all(promises) as unknown) as IPromise<void>,
            ])
          }
        })
          .then(() => {
            $scope.$applyAsync(() => {
              $window.print()
            })
          })
          .then(() => {
            if (env.isProduction()) {
              this._removePrinterElementTimer = $timeout(
                () => this._removePrinterElement(),
                WR_PRINTER_TIMEOUT
              )
            }
          })
      }

      /**
       * Removes the latest print directive appended to the container.
       *
       * @private
       * @method _removePrinterElement
       */
      this._removePrinterElement = function () {
        const el = ($document[0] as Document).getElementById(WR_PRINTER_ID)
        el && el.remove()
      }

      const unbindPrintListener = $rootScope.$on(EVENT.PRINT, (event, data) =>
        this._print(data)
      )

      $scope.$on('$destroy', unbindPrintListener)
    },
  }
})
